import { useState, type FormEvent } from "react";
import { Copy, KeyRound, Plus, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../api";
import { useApi } from "../hooks";
import Dialog from "./Dialog";
import { StatusBadge } from "./Ui";
import { inviteText, type Invite } from "../portal-invite";
import { passwordValidationError } from "../password-validation";

type Login = {
  id: number;
  email: string;
  full_name: string;
  active: boolean;
  last_login: string | null;
  created_at: string;
};
type Issued = Invite;

/** What to send the client: where to sign in, the email, and the password shown once. */
function IssuedPassword({ issued, onClose }: { issued: Issued; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  const signIn = `${window.location.origin}/client-login`;
  const text = inviteText(issued, window.location.origin);
  return (
    <Dialog
      title={issued.created ? "Login created" : "Password reset"}
      description="The password shows only this once. Copy the invite and send it to them now by email or text."
      onClose={onClose}
    >
      <div className="operation-form record-form">
        <dl className="issued-login">
          <div><dt>Sign in at</dt><dd>{signIn}</dd></div>
          <div><dt>Email</dt><dd>{issued.email}</dd></div>
          <div><dt>Temporary password</dt><dd><code>{issued.password}</code></dd></div>
        </dl>
        <div className="operation-actions">
          <button
            type="button"
            className="button secondary"
            onClick={async () => {
              setCopyError("");
              try {
                await navigator.clipboard.writeText(text);
                setCopied(true);
              } catch { setCopied(false); setCopyError("Copy failed. Select and copy the sign-in details above."); }
            }}
          >
            <Copy aria-hidden="true" /> {copied ? "Copied" : "Copy invite"}
          </button>
          <button type="button" className="button primary" onClick={onClose}>Done</button>
        </div>
        {copyError && <p className="inline-notice danger" role="alert">{copyError}</p>}
      </div>
    </Dialog>
  );
}

function AddLogin({ clientId, clientName, onClose, onCreated }: { clientId: number; clientName: string; onClose: () => void; onCreated: (issued: Issued | null) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const f = new FormData(e.currentTarget);
    const password = String(f.get("password") || "");
    const invalid = password ? passwordValidationError(password) : null;
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    setError("");
    try {
      const r = await api<{ login: Login; temporary_password?: string }>(`/api/portal-logins/${clientId}`, {
        method: "POST",
        body: JSON.stringify({ full_name: f.get("full_name"), email: f.get("email"), ...(password ? { password } : {}) }),
      });
      onCreated(r.temporary_password ? { email: r.login.email, password: r.temporary_password, created: true, name: r.login.full_name, clientName } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The login could not be created");
      setBusy(false);
    }
  }
  return (
    <Dialog title="Add portal login" description={`Signs in to ${clientName}'s portal only: their performance, reports and requests.`} onClose={onClose}>
      <form className="operation-form record-form" onSubmit={submit}>
        <div className="form-pair">
          <label>
            <span className="field-label">Name<span className="field-required" aria-hidden="true"> *</span></span>
            <input name="full_name" required maxLength={255} placeholder="Full name" />
          </label>
          <label>
            <span className="field-label">Email<span className="field-required" aria-hidden="true"> *</span></span>
            <input name="email" type="email" required maxLength={254} autoCapitalize="none" spellCheck={false} placeholder="owner@client.com" />
          </label>
        </div>
        <label>
          <span className="field-label">Password</span>
          <input name="password" type="text" minLength={12} maxLength={72} autoComplete="off" spellCheck={false} placeholder="Leave blank to generate one" />
          <small className="field-hint">Blank makes a strong temporary password and shows it once.</small>
        </label>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button primary" disabled={busy}>{busy ? "Creating…" : "Create login"}</button>
        </div>
      </form>
    </Dialog>
  );
}

/** Client profile panel: who at the client can sign in to their portal. Superadmin only. */
export default function PortalLogins({ clientId, clientName }: { clientId: number; clientName: string }) {
  const logins = useApi<Login[]>(`/api/portal-logins/${clientId}`);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [notice, setNotice] = useState("");
  const [invite, setInvite] = useState<Login | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  async function act(login: Login, action: "reset" | "toggle" | "invite") {
    setBusyId(login.id);
    setNotice("");
    try {
      if (action === "invite") {
        const r = await api<{message:string}>(`/api/portal-logins/${clientId}/${login.id}/invite`, {method:"POST"});
        setNotice(r.message);setInvite(null);
      } else if (action === "reset") {
        const r = await api<{ temporary_password: string }>(`/api/portal-logins/${clientId}/${login.id}/reset`, { method: "POST" });
        setIssued({ email: login.email, password: r.temporary_password, created: false, name: login.full_name, clientName });
      } else {
        await api(`/api/portal-logins/${clientId}/${login.id}`, { method: "PUT", body: JSON.stringify({ active: !login.active }) });
        setNotice(login.active ? `${login.full_name} can no longer sign in.` : `${login.full_name} can sign in again.`);
      }
      logins.reload();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "That change could not be saved");
    } finally {
      setBusyId(null);
    }
  }
  const rows = logins.data || [];
  return (
    <section className="panel detail-wide portal-logins">
      <div className="panel-heading">
        <div>

          <h2>Portal logins</h2>
        </div>
        <div className="detail-heading-actions">
          <span className="panel-meta">{rows.filter((l) => l.active).length} with access</span>
          <Link className="button secondary" to={`/portal?client_id=${clientId}`}>Preview portal</Link>
          <button className="button secondary" onClick={() => setAdding(true)}><Plus aria-hidden="true" /> Add login</button>
        </div>
      </div>
      {notice && <p className="inline-notice" role="status">{notice}</p>}
      {logins.error ? (
        <p className="inline-notice danger" role="alert">{logins.error}</p>
      ) : logins.loading && !logins.data ? (
        <p className="workflow-note" role="status">Loading portal logins…</p>
      ) : !rows.length ? (
        <p className="workflow-note">
          No one at {clientName} can sign in yet. Add a login and they can see their results and send you new tasks without leaving the portal.
        </p>
      ) : (
        <ul className="plain-list login-list">
          {rows.map((l) => (
            <li key={l.id} className={l.active ? "" : "inactive"}>
              <div>
                <strong>{l.full_name}</strong>
                <span>{l.email} · {l.last_login ? `Last signed in ${formatDate(l.last_login)}` : "Never signed in"}</span>
              </div>
              <div className="login-actions">
                <StatusBadge tone={l.active && l.last_login ? "success" : "neutral"}>{!l.active ? "Deactivated" : l.last_login ? "Active" : "Not signed in yet"}</StatusBadge>
                {l.active && <button className="button secondary" disabled={busyId===l.id} onClick={()=>{setNotice("");setInvite(l);}}><Mail aria-hidden="true"/>{l.last_login ? 'Email password link' : 'Email setup link'}</button>}
                {l.active && (
                  <button className="button secondary" disabled={busyId === l.id} onClick={() => void act(l, "reset")}>
                    <KeyRound aria-hidden="true" /> Reset password
                  </button>
                )}
                <button className="button secondary" disabled={busyId === l.id} onClick={() => void act(l, "toggle")}>
                  {l.active ? "Deactivate" : "Reactivate"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {invite && <Dialog title={invite.last_login ? 'Email password reset link' : 'Email password setup link'} description={`Send a 30-minute password ${invite.last_login ? 'reset' : 'setup'} link to ${invite.email} for ${clientName}. Their current password keeps working until they choose a new one.`} onClose={()=>{if(busyId!==invite.id)setInvite(null);}}>{notice&&<p className="inline-notice danger" role="alert">{notice}</p>}<div className="operation-actions"><button className="button secondary" disabled={busyId===invite.id} onClick={()=>setInvite(null)}>Cancel</button><button className="button" disabled={busyId===invite.id} onClick={()=>void act(invite,"invite")}>{busyId===invite.id?'Sending…':invite.last_login?'Send reset email':'Send setup email'}</button></div></Dialog>}
      {adding && (
        <AddLogin
          clientId={clientId}
          clientName={clientName}
          onClose={() => setAdding(false)}
          onCreated={(result) => {
            setAdding(false);
            logins.reload();
            if (result) setIssued(result);
            else setNotice("Login created with the password you set.");
          }}
        />
      )}
      {issued && <IssuedPassword issued={issued} onClose={() => setIssued(null)} />}
    </section>
  );
}
